'use client'

import { toast } from '@/components/common/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { TextArea } from '@/components/ui/textarea'
import { useContactMutation } from '@/hooks/react-query/contact/use-contact.mutation'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent } from '@/lib/gtm/type'
import { AffiliateTracker } from '@/lib/tracking/affiliate-tracker'
import { validatePhone } from '@/lib/utils/validation'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import * as z from 'zod'

const getExtendedContactFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, t('name_required')),
    phone: z.string().refine(
      (value) => {
        const phoneValidate = validatePhone(value)
        return phoneValidate.valid
      },
      {
        message: t('invalid_phone'),
      }
    ),
    quantity: z.string().min(1, t('quantity_required')),
    note: z.string().optional(),
    region: z.string().optional(),
  })

type ExtendedContactFormData = {
  name: string
  phone: string
  quantity: string
  note?: string
  region?: string
}

interface CxpExtendedContactFormProps {
  solutionOptions: Array<{ value: string; label: string; order: number }>
  quantityOptions: Array<{ value: string; label: string; order: number }>
  formHeading: string
  formBadgeText: string
}

export function CxpExtendedContactForm({
  solutionOptions,
  quantityOptions,
  formHeading,
  formBadgeText,
}: CxpExtendedContactFormProps) {
  const { mutateAsync: sendContact, isPending } = useContactMutation()
  const t = useTranslations('cxp_contact_form')
  const affiliateData = AffiliateTracker.getInstance().getTrackingData()
  const trackEvent = useTrackEvent()

  const form = useForm<ExtendedContactFormData>({
    resolver: zodResolver(getExtendedContactFormSchema(t)),
    defaultValues: {
      name: '',
      phone: '',
      quantity: '',
      note: '',
      region: '',
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = form

  const onSubmit = async (formData: ExtendedContactFormData) => {
    try {
      await sendContact({
        name: formData.name,
        phone: formData.phone,
        quantity: formData.quantity,
        type: 'coolxprint',
        note: formData.note,
        region: formData.region,
        utm_source: affiliateData?.aff_channel || '',
        utm_medium: affiliateData?.utm_medium || '',
        utm_campaign: affiliateData?.utm_campaign || '',
        utm_term: affiliateData?.utm_term || '',
        utm_content: affiliateData?.utm_content || '',
      })

      toast.message({
        title: t('success_title'),
        message: t('success_message'),
      })
      reset()

      trackEvent({
        event: ETrackingEvent.Conversion,
        send_to: 'AW-11221863705/0iymCNa9oq4YEJmagOcp',
      })

      trackEvent({
        pageType: 'Confirmation',
      } as never)

      trackEvent({
        event: ETrackingEvent.SubmitOct,
        oct_thankyou_phone: formData.phone,
      })

      trackEvent({
        event: ETrackingEvent.FormSubmissionSuccess,
        serviceOrder: 'true',
      })

      trackEvent({ event: ETrackingEvent.Remarketing })
    } catch {
      toast.message({
        title: t('fail_title'),
        message: t('fail_message'),
      })
    }
  }

  return (
    <div className='rounded-xl bg-neutral-50 p-4 sm:rounded-2xl sm:p-5 md:p-6 lg:p-8 xl:p-12'>
      <div className='mb-3 md:mb-8'>
        <div className='mb-2.5 inline-flex w-fit items-center justify-center rounded-full bg-[#ff4e00] px-3 py-1.5 sm:mb-3 sm:px-4 sm:py-2 md:mb-4 md:px-6 md:py-3'>
          <span className='font-formaDjr text-sm font-medium leading-none text-white sm:text-base md:text-xl'>
            {formBadgeText}
          </span>
        </div>
        <h2 className='font-formaDjr text-[24px] font-medium leading-tight tracking-tight text-neutral-900 sm:text-[28px] md:text-[32px] lg:text-[40px] xl:text-[45px] 2xl:text-[50px]'>
          {formHeading}
        </h2>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className='space-y-3 md:space-y-6'
      >
        {/* Name and Phone Row */}
        <div className='grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 md:gap-6'>
          <div>
            <label className='mb-1.5 block font-sans text-xs text-neutral-900 sm:mb-2 sm:text-sm'>
              {t('name_label')} <span className='text-error-500'>*</span>
            </label>
            <Input
              {...register('name')}
              placeholder={t('name_placeholder')}
              error={errors.name}
            />
          </div>
          <div>
            <label className='mb-1.5 block font-sans text-xs text-neutral-900 sm:mb-2 sm:text-sm'>
              {t('phone_label')} <span className='text-error-500'>*</span>
            </label>
            <Input
              {...register('phone')}
              placeholder={t('phone_placeholder')}
              error={errors.phone}
            />
          </div>
        </div>

        {/* Quantity Row */}
        <div className='grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 md:gap-6'>
          <div>
            <label className='mb-1.5 block font-sans text-xs text-neutral-900 sm:mb-2 sm:text-sm'>
              {t('quantity_label')} <span className='text-error-500'>*</span>
            </label>
            <Select
              value={watch('quantity')}
              onValueChange={(value) => setValue('quantity', value)}
              placeholder={t('quantity_placeholder')}
              options={quantityOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              className={{
                trigger: 'h-10 text-xs leading-4 sm:h-11 sm:text-sm',
              }}
              error={errors.quantity}
            />
          </div>
          <div>
            <label className='mb-1.5 block font-sans text-xs text-neutral-900 sm:mb-2 sm:text-sm'>
              {t('region_label')}
            </label>
            <Select
              value={watch('region')}
              onValueChange={(value) => setValue('region', value)}
              placeholder={t('region_placeholder')}
              options={[
                { value: 'Miền Bắc', label: 'Miền Bắc' },
                { value: 'Miền Trung', label: 'Miền Trung' },
                { value: 'Miền Nam', label: 'Miền Nam' },
              ]}
              error={errors.region}
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className='mb-1.5 block font-sans text-xs text-neutral-900 sm:mb-2 sm:text-sm'>
            {t('note_label')}
          </label>
          <TextArea
            {...register('note')}
            placeholder={t('note_placeholder')}
            className='min-h-[0px] rounded-xl sm:min-h-[80px] sm:rounded-2xl'
            rows={3}
            error={errors.note}
          />
        </div>

        {/* Submit Button */}
        <Button
          type='submit'
          size='large'
          className='flex w-full items-center justify-center gap-2 rounded-full bg-neutral-900 text-white hover:bg-neutral-800'
          disabled={isPending}
          ga-tracking-value='CXP_submit_contact_form'
          ga-tracking-label='CXP_submit_contact_form'
        >
          <span className='text-sm font-bold uppercase sm:text-base'>
            {isPending ? t('submit_sending') : t('submit')}
          </span>
          <svg
            width='19'
            height='20'
            viewBox='0 0 19 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='h-4 w-4 sm:h-5 sm:w-5'
          >
            <path
              d='M0 10H17M17 10L9 2M17 10L9 18'
              stroke='currentColor'
              strokeWidth='2'
            />
          </svg>
        </Button>
      </form>
    </div>
  )
}
